import * as React from 'react';
import { browserHistory } from 'react-router';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import * as Tgcc from '../tgccGlobal';
import * as Request from '../tgccRequest';

interface ConfirmEmailProps {
  dispatch?: Dispatch<any>;
  loginData: Tgcc.LoginData;
  userInfo: Tgcc.UserInfo;
}

interface ConfirmEmailState {
  processing: boolean;
  success: boolean;
  error: string | null;
}

class ConfirmEmail extends React.Component<ConfirmEmailProps, ConfirmEmailState> {
  constructor(props: ConfirmEmailProps) {
    super(props);
    this.state = {
      processing: true,
      success: false,
      error: null,
    };
  }

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      this.setState({
        processing: false,
        success: false,
        error: 'Invalid or missing token.',
      });
      return;
    }
    this.confirmEmail(token);
  }

  confirmEmail(token: string) {
    Request.confirmEmailUpdate(
      token,
      (response) => {
        const { division, accessToken, expires, newEmail } = response;
        if (!division || !accessToken || !expires) {
          this.setState({
            processing: false,
            success: false,
            error: 'Failed to retrieve division or authentication token.',
          });
          return;
        }
  
        // 1) Immediately set the new JWT for all future requests
        Request.setAuthToken(accessToken);
  
        // 2) Build the fresh LoginData
        const updatedLoginData: Tgcc.LoginData = {
          ...this.props.loginData,
          username: newEmail || this.props.userInfo.email,
          token: accessToken,
          expires,
        };
  
        // 3) Build the fresh UserInfo
        const updatedUserInfo: Tgcc.UserInfo = {
          ...this.props.userInfo,
          email: newEmail || this.props.userInfo.email,
        };
  
        // 4) Dispatch into Redux (if connected)
        if (this.props.dispatch) {
          const { dispatch } = this.props;
  
          // clear any dialog or forward state
          dispatch({ type: Tgcc.StateAction.DialogAction, payload: null });
  
          // update LoginData
          dispatch({
            type: Tgcc.StateAction.Login,
            payload: updatedLoginData,
          });
  
          // update UserInfo
          dispatch({
            type: Tgcc.StateAction.SetUserInfo,
            payload: updatedUserInfo,
          });
  
          // persist both into localStorage
          Tgcc.SaveUserData(updatedUserInfo, updatedLoginData);
        }
  
        // 5) Show success & then redirect
        this.setState(
          { processing: false, success: true, error: null },
          () => {
            setTimeout(() => {
              switch (division.toLowerCase()) {
                case 'campus':
                  browserHistory.push(Tgcc.Routes.CampusDashboard);
                  break;
                case 'corporate':
                  browserHistory.push(Tgcc.Routes.DashBoard);
                  break;
                case 'indigo':
                  browserHistory.push(Tgcc.Routes.IndigoDashboard);
                  break;
                default:
                  this.setState({
                    processing: false,
                    success: false,
                    error: 'Unknown division.',
                  });
              }
            }, 2000);
          }
        );
      },
      (err) => {
        this.setState({
          processing: false,
          success: false,
          error: err,
        });
      }
    );
  }
  

  render() {
    const { processing, success, error } = this.state;

    const containerStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f7f9fc',
      fontFamily: 'Arial, sans-serif',
      color: '#333',
      padding: '20px',
    };
    const logoStyle: React.CSSProperties = {
      width: '150px',
      marginBottom: '20px',
    };
    const cardStyle: React.CSSProperties = {
      background: '#fff',
      padding: '30px',
      borderRadius: '10px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      textAlign: 'center',
      maxWidth: '500px',
      width: '100%',
    };
    const headerStyle: React.CSSProperties = {
      fontSize: '1.8rem',
      fontWeight: 'bold',
      color: success ? '#28a745' : '#4a90e2',
      marginBottom: '10px',
    };
    const messageStyle: React.CSSProperties = {
      fontSize: '1rem',
      color: '#555',
      marginBottom: '20px',
    };
    const errorStyle: React.CSSProperties = {
      color: '#d9534f',
      fontWeight: 'bold',
      fontSize: '1rem',
      marginBottom: '20px',
    };
    const loadingSpinnerStyle: React.CSSProperties = {
      width: '40px',
      height: '40px',
      border: '4px solid #f3f3f3',
      borderRadius: '50%',
      borderTop: '4px solid #4a90e2',
      animation: 'spin 1s linear infinite',
      marginBottom: '20px',
    };
    const spinnerAnimation = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    const successStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    };
    const checkmarkStyle: React.CSSProperties = {
      fontSize: '3rem',
      color: '#28a745',
      marginBottom: '10px',
    };

    return (
      <div style={containerStyle}>
        <style>{spinnerAnimation}</style>
        <img
          src={Tgcc.ResolveUrl('~/images/PrimaryLogo.svg')}
          alt="Logo"
          style={logoStyle}
        />
        <div style={cardStyle}>
          {processing && (
            <div>
              <div style={loadingSpinnerStyle}></div>
              <h2 style={headerStyle}>Processing your request...</h2>
              <p style={messageStyle}>Please wait while we confirm your email.</p>
            </div>
          )}
          {success && (
            <div style={successStyle}>
              <div style={checkmarkStyle}>✔</div>
              <h2 style={headerStyle}>Email Verified</h2>
              <p style={messageStyle}>Redirecting you to your dashboard...</p>
            </div>
          )}
          {error && (
            <div>
              <h2 style={headerStyle}>Error</h2>
              <p style={errorStyle}>{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: Tgcc.GlobalState) => ({
  loginData: state.LoginData,
  userInfo: state.UserInfo,
});

export default connect(mapStateToProps)(ConfirmEmail);
